import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Crown, 
  Star, 
  Shield, 
  CheckCircle, 
  Clock,
  ArrowRight, 
  ArrowLeft,
  Zap,
  Eye,
  Phone,
  Award,
  X,
  ShieldCheck
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import IPGCheckout from './IPGCheckout';
import './MembershipSelection.css';
import { API_BASE_URL } from '../../utils/api';


interface MembershipTier {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price: number;
  duration_months: number;
  service_limit: number;
  phone_visible: boolean;
  verified_badge: boolean;
  priority_support: boolean;
  enhanced_visibility: boolean;
  features: string[];
  currency?: string;
  billing_interval?: string;
}

const getCurrencySymbol = (currencyCode: string): string => {
  const code = (currencyCode || 'USD').toUpperCase();
  const symbols: { [key: string]: string } = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', HKD: 'HK$', SGD: 'S$', SEK: 'kr', ARS: '$', BRL: 'R$',
    CLP: '$', CNY: '¥', COP: '$', CZK: 'Kč', DKK: 'kr', HUF: 'Ft',
    ILS: '₪', INR: '₹', KRW: '₩', MXN: '$', NOK: 'kr', NZD: 'NZ$',
    PEN: 'S/.', PLN: 'zł', RUB: '₽', THB: '฿', TRY: '₺', TWD: 'NT$',
    UAH: '₴', VND: '₫', ZAR: 'R'
  };
  return symbols[code] || '$';
};

const MembershipSelection: React.FC = () => {
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTierForPayment, setSelectedTierForPayment] = useState<MembershipTier | null>(null);
  const [talentProfile, setTalentProfile] = useState<any>(null);

  const [autoOpened, setAutoOpened] = useState(false);
  
  const { token, talent: authTalent } = useTalentAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchTalentProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/talent/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.talent) {
        setTalentProfile(data.talent);
      }
    } catch (err) {
      console.error('Error fetching talent profile in MembershipSelection:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchMembershipTiers();
    if (token) {
      fetchTalentProfile();
    }
  }, [token, fetchTalentProfile]);

  useEffect(() => {
    if (showPaymentModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [showPaymentModal]);

  const fetchMembershipTiers = async () => {
    try {
      // First try fetching active plans created by admin from subscription-plans endpoint
      const plansRes = await fetch(`${API_BASE_URL}/subscription-plans`);
      const plansData = await plansRes.json();

      if (plansData.success && Array.isArray(plansData.plans) && plansData.plans.length > 0) {
        const mappedTiers = plansData.plans.map((p: any) => ({
          id: p.id,
          name: p.plan_name.toLowerCase().replace(/\s+/g, '_'),
          display_name: p.plan_name,
          description: p.description,
          price: parseFloat(p.amount),
          duration_months: p.billing_interval === 'yearly' ? 12 : (p.billing_interval === 'hourly' ? 0 : 1),
          service_limit: -1,
          phone_visible: true,
          verified_badge: true,
          priority_support: true,
          enhanced_visibility: true,
          features: Array.isArray(p.features) ? p.features : [],
          paddle_price_id: p.paddle_price_id,
          currency: p.currency || 'USD',
          billing_interval: p.billing_interval
        }));
        setMembershipTiers(mappedTiers);
        return;
      }

      // Fallback to legacy membership tiers
      const response = await fetch(`${API_BASE_URL}/membership/tiers`);
      const data = await response.json();
      
      if (data.success) {
        setMembershipTiers(data.tiers);
      } else {
        setError('Failed to load membership plans');
      }
    } catch (error) {
      console.error('Error fetching membership tiers:', error);
      setError('Failed to load membership plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (tierId: number) => {
    console.log('handleSelectPlan called with tierId:', tierId);
    
    if (!token) {
      console.log('No token found, redirecting to login');
      navigate('/talent/login');
      return;
    }

    const selectedTierData = membershipTiers.find(tier => tier.id === tierId);
    console.log('Selected tier data:', selectedTierData);
    
    if (!selectedTierData) {
      setError('Invalid plan selected');
      return;
    }

    // If it's a free plan, activate it directly
    if (selectedTierData.price === 0) {
      console.log('Free plan selected, activating directly');
      setSelectedTier(tierId);
      setProcessing(true);

      try {
        const response = await fetch(`${API_BASE_URL}/membership/upgrade`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            tierName: selectedTierData.name,
            paymentMethod: 'free',
            transactionId: `free_${Date.now()}_${tierId}`
          })
        });

        const data = await response.json();

        if (data.success) {
          setProcessing(false);
          navigate('/talent/membership/success');
        } else {
          setError(data.message || 'Failed to activate free plan');
          setProcessing(false);
        }
      } catch (error) {
        console.error('Error selecting plan:', error);
        setError('Failed to process plan selection');
        setProcessing(false);
      }
    } else {
      // For paid plans, show payment modal
      console.log('Paid plan selected, showing payment modal');
      console.log('Setting selectedTierForPayment to:', selectedTierData);
      setSelectedTierForPayment(selectedTierData);
      setShowPaymentModal(true);
      console.log('Payment modal should be visible now');
    }
  };

  useEffect(() => {
    if (!loading && membershipTiers.length > 0 && !autoOpened) {
      const state = location.state as { selectedTierId?: number; selectedTierName?: string; selectedTierDisplay?: string; autoOpenPayment?: boolean } | null;
      if (state?.autoOpenPayment) {
        let matched = membershipTiers.find(t => t.id === state.selectedTierId);
        if (!matched && state.selectedTierName) {
          const searchName = state.selectedTierName.toLowerCase().trim();
          matched = membershipTiers.find(t => t.name.toLowerCase().trim() === searchName || t.display_name.toLowerCase().trim() === searchName);
        }
        if (!matched && state.selectedTierDisplay) {
          const searchDisplay = state.selectedTierDisplay.toLowerCase().trim();
          matched = membershipTiers.find(t => t.display_name.toLowerCase().trim() === searchDisplay);
        }
        if (matched) {
          setAutoOpened(true);
          handleSelectPlan(matched.id);
        }
      }
    }
  }, [loading, membershipTiers, location.state, autoOpened]);

  const handlePaymentSuccess = async (transactionId: string, gateway: string = paymentGateway) => {
    console.log('handlePaymentSuccess called with transactionId:', transactionId, 'gateway:', gateway);
    console.log('selectedTierForPayment:', selectedTierForPayment);
    
    if (!selectedTierForPayment) {
      console.log('No selectedTierForPayment found');
      return;
    }

    try {
      console.log('Sending membership upgrade request...');
      const response = await fetch(`${API_BASE_URL}/membership/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tierName: selectedTierForPayment.display_name || selectedTierForPayment.name,
          planId: selectedTierForPayment.id,
          amount: selectedTierForPayment.price,
          paymentMethod: gateway,
          transactionId
        })
      });

      const data = await response.json();
      console.log('Upgrade response:', data);

      if (data.success) {
        setShowPaymentModal(false);
        navigate('/talent/membership/success');
      } else {
        setError(data.message || 'Failed to complete membership upgrade');
        setShowPaymentModal(false);
      }
    } catch (error) {
      console.error('Error handling payment success:', error);
      setError('Failed to complete membership upgrade');
      setShowPaymentModal(false);
    }
  };

  const getTierIcon = (tierName: string) => {
    switch (tierName.toLowerCase()) {
      case 'standard':
        return <Star className="tier-icon" />;
      case 'premium':
        return <Crown className="tier-icon" />;
      default:
        return <Shield className="tier-icon" />;
    }
  };

  const formatPrice = (price: number, durationMonths: number, currencyCode: string = 'USD', billingInterval?: string) => {
    if (price === 0) return 'Free';
    let interval = durationMonths === 12 ? 'year' : 'month';
    if (billingInterval) {
      const lower = billingInterval.toLowerCase();
      if (lower === 'yearly') interval = 'year';
      else if (lower === 'weekly') interval = 'week';
      else if (lower === 'daily') interval = 'day';
      else if (lower === 'hourly') interval = 'hour';
      else interval = 'month';
    }
    const formattedPrice = price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);
    return `${getCurrencySymbol(currencyCode)}${formattedPrice}/${interval}`;
  };

  if (loading) {
    return (
      <div className="membership-selection-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading membership plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="membership-selection-container">
      <div className="membership-selection-content">
        <div className="selection-header d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4" style={{ position: 'relative' }}>
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-secondary fw-semibold d-flex align-items-center gap-2 px-3 py-2"
              style={{ borderRadius: '10px', backgroundColor: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer' }}
              onClick={() => navigate('/talent/membership')}
            >
              <ArrowLeft size={16} /> Back to Overview
            </button>
            <div>
              <h1 style={{ margin: 0 }}>Choose Your Membership Plan</h1>
              <p style={{ margin: 0, marginTop: '4px', opacity: 0.85 }}>Select a plan to unlock powerful features and grow your business</p>
            </div>
          </div>
          <button
            className="btn btn-outline-secondary fw-semibold d-flex align-items-center gap-2 px-3 py-2"
            style={{ borderRadius: '10px', backgroundColor: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'pointer' }}
            onClick={() => navigate('/talent/dashboard')}
          >
            Skip for Now <ArrowRight size={16} />
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="membership-tiers">
          {membershipTiers.map((tier) => (
            <div 
              key={tier.id} 
              className={`membership-card ${tier.name.toLowerCase()}`}
            >
              <div className="plan-card-header">
                <div className="tier-icon-container">
                  {getTierIcon(tier.name)}
                </div>
                <h3 className="tier-title">{tier.display_name ? (tier.display_name.charAt(0).toUpperCase() + tier.display_name.slice(1)) : 'Premium'}</h3>
                {tier.description && <p className="tier-description">{tier.description}</p>}
              </div>

              <div className="card-pricing">
                <span className="price">{formatPrice(tier.price, tier.duration_months, tier.currency, tier.billing_interval)}</span>
              </div>

              <div className="card-features">
                {tier.features && tier.features.length > 0 ? (
                  tier.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="feature-item">
                      <CheckCircle size={16} />
                      <span>{tier.service_limit === -1 ? 'Unlimited' : tier.service_limit} Services</span>
                    </div>
                    {tier.phone_visible && (
                      <div className="feature-item">
                        <CheckCircle size={16} />
                        <span>Phone Visible to Customers</span>
                      </div>
                    )}
                    {tier.verified_badge && (
                      <div className="feature-item">
                        <CheckCircle size={16} />
                        <span>Verified Badge</span>
                      </div>
                    )}
                    {tier.enhanced_visibility && (
                      <div className="feature-item">
                        <CheckCircle size={16} />
                        <span>Enhanced Visibility</span>
                      </div>
                    )}
                    {tier.priority_support && (
                      <div className="feature-item">
                        <CheckCircle size={16} />
                        <span>Priority Support</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="card-actions">
                <button
                  className={`select-plan-btn ${selectedTier === tier.id && processing ? 'processing' : ''}`}
                  onClick={() => handleSelectPlan(tier.id)}
                  disabled={processing && selectedTier === tier.id}
                >
                  {processing && selectedTier === tier.id ? (
                    <>
                      <div className="button-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {tier.price === 0 ? 'Start Free' : (membershipTiers.length > 1 ? 'Select Plan' : 'Next')}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Modal */}
        {/* Payment Modal */}
        {showPaymentModal && selectedTierForPayment && (
          <div 
            className="checkout-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(4px)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              boxSizing: 'border-box',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowPaymentModal(false);
            }}
          >
            <div 
              className="checkout-container"
              style={{
                backgroundColor: '#ffffff',
                color: '#0f172a',
                width: '100%',
                maxWidth: '520px',
                maxHeight: 'calc(100vh - 32px)',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                position: 'relative',
                boxSizing: 'border-box'
              }}
            >
              {/* Header */}
              <div 
                style={{
                  background: 'linear-gradient(135deg, #185FA5 0%, #0d386b 100%)',
                  color: '#ffffff',
                  padding: '16px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <ShieldCheck size={20} className="text-info" />
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, fontSize: '16px', color: '#ffffff' }}>
                      Upgrade to {selectedTierForPayment.display_name || selectedTierForPayment.name}
                    </h5>
                    <span style={{ fontSize: '12px', opacity: 0.9, color: '#e0f2fe', marginTop: '2px', display: 'block' }}>
                      {getCurrencySymbol(selectedTierForPayment.currency || '')}{selectedTierForPayment.price.toFixed(2)}/{
                        selectedTierForPayment.billing_interval === 'hourly' ? 'hour' : 
                        selectedTierForPayment.billing_interval === 'daily' ? 'day' : 
                        selectedTierForPayment.billing_interval === 'weekly' ? 'week' : 
                        selectedTierForPayment.billing_interval === 'yearly' ? 'year' : 
                        'month'
                      } • Cancel anytime
                    </span>
                  </div>
                </div>
                <button 
                  type="button" 
                  onClick={() => setShowPaymentModal(false)}
                  style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', opacity: 0.9 }}
                >
                  <X size={22} />
                </button>
              </div>

              {/* Body */}
              <div style={{ padding: '20px 24px 24px 24px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
                {/* Fiserv IPG Secure Hosted Payment Form */}
                <IPGCheckout
                  amount={selectedTierForPayment.price}
                  tierName={selectedTierForPayment.display_name || selectedTierForPayment.name}
                  planId={selectedTierForPayment.id}
                  currency={selectedTierForPayment.currency || 'USD'}
                  onCancel={() => setShowPaymentModal(false)}
                />
              </div>
            </div>
          </div>
        )}



      </div>
    </div>
  );
};

export default MembershipSelection;
